<template>
  <div class="flex flex-col gap-6">
    <form class="rounded-md border p-4" @submit.prevent="searchConversations">
      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div class="flex flex-col gap-2 md:col-span-2">
          <Label for="chat-admin-search">{{ $t('chat.admin.search') }}</Label>
          <Input id="chat-admin-search" v-model="filters.search" placeholder="Message, title, user name, or email" />
        </div>

        <UserSelect v-model="selectedUserFilter" :label="$t('chat.admin.userId')" placeholder="Search participant" @selected="setUserFilter" @cleared="clearUserFilter" />

        <div class="flex flex-col gap-2">
          <Label>{{ $t('chat.admin.type') }}</Label>
          <Select v-model="filters.type">
            <SelectTrigger>
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                Any type
              </SelectItem>
              <SelectItem value="direct">
                Direct
              </SelectItem>
              <SelectItem value="group">
                Group
              </SelectItem>
              <SelectItem value="support">
                Support
              </SelectItem>
              <SelectItem value="system">
                System
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div class="flex flex-col gap-2">
          <Label for="chat-admin-message-from">{{ $t('chat.admin.messageFrom') }}</Label>
          <Input id="chat-admin-message-from" v-model="filters.message_from" type="datetime-local" />
        </div>

        <div class="flex flex-col gap-2">
          <Label for="chat-admin-message-until">{{ $t('chat.admin.messageUntil') }}</Label>
          <Input id="chat-admin-message-until" v-model="filters.message_until" type="datetime-local" />
        </div>
      </div>

      <div class="mt-4 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" :disabled="isLoading" @click="resetFilters">
          <RotateCcw class="h-4 w-4" />
          {{ $t('chat.admin.reset') }}
        </Button>
        <Button type="submit" :disabled="isLoading">
          <Search class="h-4 w-4" />
          {{ $t('chat.admin.search') }}
        </Button>
      </div>
    </form>

    <Alert v-if="errorMessage" variant="destructive">
      <AlertDescription>{{ errorMessage }}</AlertDescription>
    </Alert>

    <div class="overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{{ $t('chat.admin.conversation') }}</TableHead>
            <TableHead>{{ $t('chat.admin.participants') }}</TableHead>
            <TableHead>{{ $t('chat.admin.lastMessage') }}</TableHead>
            <TableHead class="text-right">
              {{ $t('chat.admin.activity') }}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-for="conversation in conversations" :key="conversation.id">
            <TableCell class="align-top">
              <div class="flex min-w-0 flex-col gap-1">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ conversationTitle(conversation) }}</span>
                  <Badge variant="secondary" class="capitalize">
                    {{ conversation.type }}
                  </Badge>
                </div>
                <span class="text-xs text-muted-foreground">#{{ conversation.id }} · {{ formatDateTime(conversation.created_at) }}</span>
              </div>
            </TableCell>
            <TableCell class="align-top">
              <div class="flex max-w-sm flex-wrap gap-1">
                <Badge v-for="participant in activeParticipants(conversation)" :key="participant.id" variant="outline">
                  {{ participant.user?.name || `User #${participant.user_id}` }}
                </Badge>
              </div>
            </TableCell>
            <TableCell class="max-w-md align-top">
              <div class="flex flex-col gap-1">
                <span class="truncate text-sm">{{ lastMessagePreview(conversation) }}</span>
                <span class="text-xs text-muted-foreground">{{ formatDateTime(conversation.last_message_at) }}</span>
              </div>
            </TableCell>
            <TableCell class="align-top text-right">
              <div class="flex flex-col gap-1 text-sm">
                <span>{{ conversation.messages_count || 0 }} messages</span>
                <span class="text-muted-foreground">{{ conversation.participants_count || 0 }} participants</span>
              </div>
            </TableCell>
          </TableRow>
          <TableRow v-if="!isLoading && conversations.length === 0">
            <TableCell colspan="4" class="h-24 text-center text-sm text-muted-foreground">
              {{ $t('chat.admin.noConversations') }}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="text-sm text-muted-foreground">
        {{ paginationSummary }}
      </p>
      <div class="flex gap-2">
        <Button variant="outline" size="sm" :disabled="isLoading || page <= 1" @click="fetchConversations(page - 1)">
          {{ $t('chat.admin.previous') }}
        </Button>
        <Button variant="outline" size="sm" :disabled="isLoading || page >= lastPage" @click="fetchConversations(page + 1)">
          {{ $t('chat.admin.next') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, onMounted, reactive, ref } from 'vue'
import { RotateCcw, Search } from 'lucide-vue-next'
import { Alert, AlertDescription } from '../../../../../../resources/js/components/ui/alert/index.js'
import { Badge } from '../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../resources/js/components/ui/button/index.js'
import { Input } from '../../../../../../resources/js/components/ui/input/index.js'
import { Label } from '../../../../../../resources/js/components/ui/label/index.js'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../resources/js/components/ui/select/index.js'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../../resources/js/components/ui/table/index.js'
import UserSelect from '@User-manager/components/users/UserSelect.vue'

const emptyFilters = () => ({
  search: '',
  user_id: '',
  type: 'all',
  message_from: '',
  message_until: '',
})

const filters = reactive(emptyFilters())
const selectedUserFilter = ref(null)
const conversations = ref([])
const isLoading = ref(false)
const errorMessage = ref('')
const page = ref(1)
const perPage = ref(25)
const total = ref(0)
const lastPage = ref(1)

const paginationSummary = computed(() => {
  if (total.value === 0) {
    return '0 conversations'
  }

  const start = ((page.value - 1) * perPage.value) + 1
  const end = Math.min(page.value * perPage.value, total.value)

  return `${start}-${end} of ${total.value} conversations`
})

const searchConversations = () => {
  fetchConversations(1)
}

const resetFilters = () => {
  Object.assign(filters, emptyFilters())
  selectedUserFilter.value = null
  fetchConversations(1)
}

const setUserFilter = (user) => {
  filters.user_id = user?.id ? String(user.id) : ''
}

const clearUserFilter = () => {
  filters.user_id = ''
}

const fetchConversations = async (requestedPage = 1) => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/v1/chat/admin/conversations', {
      params: {
        ...requestParams(),
        page: requestedPage,
        per_page: perPage.value,
      },
    })

    conversations.value = response.data.data || []
    page.value = response.data.meta?.current_page || requestedPage
    perPage.value = response.data.meta?.per_page || perPage.value
    total.value = response.data.meta?.total || conversations.value.length
    lastPage.value = response.data.meta?.last_page || 1
  } catch (error) {
    errorMessage.value = errorMessageFrom(error, 'Unable to search chat conversations.')
  } finally {
    isLoading.value = false
  }
}

const requestParams = () => {
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => value !== '' && value !== null && value !== 'all')
  )
}

const conversationTitle = (conversation) => {
  if (conversation.title) {
    return conversation.title
  }

  const participants = activeParticipants(conversation)

  return participants.map((participant) => participant.user?.name || `User #${participant.user_id}`).join(', ') || 'Direct conversation'
}

const activeParticipants = (conversation) => {
  return conversation.participants?.filter((participant) => participant.left_at === null) || []
}

const lastMessagePreview = (conversation) => {
  return conversation.last_message?.body || conversation.last_message?.type || 'No messages yet'
}

const formatDateTime = (value) => {
  if (!value) {
    return ''
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages[0]
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

onMounted(() => {
  fetchConversations()
})
</script>
